import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UploadCloud } from "lucide-react";
import { receipts, UploadJob } from "../api/client";
import StatusPill from "../components/b/StatusPill";

const ACCEPT = "image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.heic,.webp,.tif,.tiff";
const TERMINAL = new Set(["done", "error"]);

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatCents(cents?: number | null, currency?: string | null): string {
  if (cents == null) return "—";
  const value = cents / 100;
  try {
    return value.toLocaleString("en-US", { style: "currency", currency: currency || "USD" });
  } catch {
    return value.toFixed(2);
  }
}

export default function Upload() {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<UploadJob | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Poll the job while it's still being processed.
  useEffect(() => {
    if (!job || TERMINAL.has(job.status)) return;
    const t = setInterval(async () => {
      try {
        const fresh = await receipts.getUploadJob(job.id);
        setJob(fresh);
      } catch {
        // transient — keep polling
      }
    }, 2000);
    return () => clearInterval(t);
  }, [job?.id, job?.status]);

  function addFiles(list: FileList | null) {
    if (!list) return;
    setFiles((prev) => [...prev, ...Array.from(list)]);
    setError(null);
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function startUpload() {
    if (files.length === 0 || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const created = await receipts.uploadFiles(files);
      setJob(created);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function reset() {
    setJob(null);
    setFiles([]);
    setError(null);
  }

  const processing = job && !TERMINAL.has(job.status);
  const pct =
    job && job.total_files > 0
      ? Math.round((job.processed_files / job.total_files) * 100)
      : 0;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Upload receipts</h1>
      </div>
      <p className="page-sub" style={{ marginBottom: 16 }}>
        Upload photos or PDFs of receipts, invoices, and bills and we'll parse them
        for you. A PDF can hold several receipts across its pages, and a single
        scan can hold several receipts at once — we'll split them into separate
        receipts automatically.
      </p>

      {error && <div className="page-error">{error}</div>}

      {/* ── Picker (hidden once a job is running) ─────────────────────── */}
      {!job && (
        <div className="detail-card" style={{ marginBottom: 16 }}>
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              addFiles(e.dataTransfer.files);
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "32px 16px",
              border: `1.5px dashed ${dragging ? "var(--primary)" : "var(--border-strong)"}`,
              borderRadius: 12,
              background: dragging ? "var(--primary-soft)" : "#f8fafc",
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            <div style={{ color: "var(--primary)", display: "flex", justifyContent: "center" }}>
            <UploadCloud size={32} strokeWidth={1.6} />
          </div>
            <div style={{ fontWeight: 600 }}>
              Drag & drop images/PDFs here, or click to choose
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              JPG, PNG, HEIC, or PDF — up to 20 files
            </div>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              style={{ display: "none" }}
              onChange={(e) => addFiles(e.target.files)}
            />
          </label>

          {files.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th style={{ textAlign: "right" }}>Size</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((f, i) => (
                    <tr key={`${f.name}-${i}`}>
                      <td>{f.name}</td>
                      <td style={{ textAlign: "right" }}>{formatBytes(f.size)}</td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => removeFile(i)}
                          disabled={uploading}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={startUpload}
                  disabled={uploading || files.length === 0}
                >
                  {uploading
                    ? "Uploading…"
                    : `Parse ${files.length} file${files.length === 1 ? "" : "s"}`}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setFiles([])}
                  disabled={uploading}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Job progress + outcome ────────────────────────────────────── */}
      {job && (
        <div className="detail-card">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0, flex: 1 }}>
              {job.status === "done"
                ? "Done"
                : job.status === "error"
                  ? "Upload failed"
                  : "Parsing your upload…"}
            </h3>
            {TERMINAL.has(job.status) && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={reset}>
                Upload more
              </button>
            )}
          </div>

          {processing && (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  height: 6,
                  borderRadius: 99,
                  background: "var(--bg-soft)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: "var(--primary)",
                    transition: "width 300ms ease",
                  }}
                />
              </div>
              <div className="muted" style={{ marginTop: 8 }}>
                {job.message ||
                  `Processed ${job.processed_files} of ${job.total_files} file(s)…`}
              </div>
            </div>
          )}

          {job.status === "error" && (
            <div className="page-error" style={{ marginTop: 12 }}>
              {job.error || "Something went wrong while parsing your upload."}
            </div>
          )}

          {TERMINAL.has(job.status) && (
            <div style={{ marginTop: 12 }}>
              <div className="muted" style={{ marginBottom: 8 }}>
                {job.message}
              </div>
              {job.receipts && job.receipts.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Type</th>
                      <th style={{ textAlign: "right" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.receipts.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <Link to={`/app/receipts/${r.id}`}>
                            {r.vendor_name || "Unknown vendor"}
                          </Link>
                          {r.discarded && (
                            <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                              (needs review — no amount found)
                            </span>
                          )}
                        </td>
                        <td>
                          <StatusPill status={r.receipt_type} />
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {formatCents(r.total_cents, r.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                job.status === "done" && (
                  <div className="page-empty">
                    No receipts could be parsed from this upload.
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
